import * as React from 'react';
import classnames from 'classnames';
import { classes } from './ReactionList.st.css';
import { KEYS } from '../../../common/keyCodes';
import { ReactionListRef, ReactionListProps } from '../types';

export const ReactionList = React.forwardRef<
  ReactionListRef,
  ReactionListProps
>(({ items, onSelect, selectedId }, ref) => {
  const listRef = React.useRef<HTMLDivElement>();
  const [activeIdx, setActiveIdx] = React.useState<number>(undefined);
  const itemRefs = React.useRef(new Map());

  React.useImperativeHandle(
    ref,
    () => ({
      focus: () => setActiveIdx(0),
      ref: listRef.current,
    }),
    [],
  );

  React.useEffect(() => setFocus(activeIdx), [activeIdx]);

  const setFocus = (idx: number) => {
    if (itemRefs.current.has(idx)) {
      itemRefs.current.get(idx).focus();
    }
  };

  const setRef = (itemRef: HTMLDivElement, idx: number) => {
    if (!itemRefs.current.has(idx)) {
      itemRefs.current.set(idx, itemRef);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent, id: string) => {
    const currIdx = activeIdx ?? 0;
    const lastIdx = itemRefs.current.size - 1;
    let nextIdx;

    switch (e.key) {
      case KEYS.ArrowLeft:
      case KEYS.ArrowLeftIE:
        nextIdx = currIdx === 0 ? lastIdx : currIdx - 1;
        e.preventDefault();
        return setActiveIdx(nextIdx);
      case KEYS.ArrowRight:
      case KEYS.ArrowRightIE:
        nextIdx = currIdx === lastIdx ? 0 : currIdx + 1;
        e.preventDefault();
        return setActiveIdx(nextIdx);
      case KEYS.Home:
        e.preventDefault();
        return setActiveIdx(0);
      case KEYS.End:
        e.preventDefault();
        return setActiveIdx(lastIdx);
      case KEYS.Spacebar:
      case KEYS.Enter:
        return onSelect(id);
      default:
        return;
    }
  };

  return (
    <div role="menu" className={classes.root} ref={listRef}>
      {items.map(({ id, icon, label }, idx) => (
        <div
          key={id}
          className={classnames(classes.item, {
            [classes.active]: activeIdx === idx,
          })}
          ref={(itemRef) => setRef(itemRef, idx)}
          onMouseUp={(e) => {
            e.preventDefault();
            onSelect(id);
          }}
          onKeyDown={(e) => handleKeyDown(e, id)}
          role="menuitem"
          tabIndex={-1}
          data-id={id}
          aria-label={label}
          aria-current={selectedId === id}
        >
          <div className={classes.iconWrapper}>{icon}</div>
          <div className={classes.itemLabel} aria-hidden="true">
            {label}
          </div>
        </div>
      ))}
    </div>
  );
});
